import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowDownRight, ArrowUpRight, Bell, BookUser, ChevronRight, ClipboardCheck, Hash, IndianRupee, Inbox, Package, RefreshCw, ShoppingBag, Sparkles, AlarmClock, Truck, Menu, MonitorSmartphone, ReceiptText, TrendingUp, Wallet, X, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney, formatQty, lineNet, planById } from '@sense/shared';
import { Button, Card, IconTile, SectionLabel, Skeleton, StatCard, tints, type Tint } from '../components/ui';
import { SyncPill } from '../components/SyncPill';
import { useUi } from '../components/ui-store';
import { useProducts, useStockMap } from '../lib/catalog';
import { useCustomers } from '../lib/customers';
import { db } from '../lib/db';
import { canManage, useSession } from '../lib/session';
import { useRole } from '../lib/cashier';
import { useOpenShift } from '../lib/shift';
import { syncNow, useSync } from '../lib/sync';
import { rangeFor, useSalesReport } from '../lib/salesReport';
import { useExpiringCount } from '../lib/expiry';
import { useSuppliers } from '../lib/suppliers';
import { api } from '../lib/api';
import { isLocalOnly, useLocalOnly } from '../lib/local';
import { SETUP_STEPS, setupDone } from '../lib/stores';
import { localeOf, useLang, useT, tr } from '../lib/i18n';

const DAY = 86400000;
const PAY_COLORS: Record<string, string> = { upi: '#0F766E', cash: '#B45309', card: '#1D4ED8', credit: '#BE185D' };
const PAY_LABELS: Record<string, string> = { upi: 'UPI', cash: 'Cash', card: 'Card', credit: 'Khata' };

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export function Home() {
  const me = useSession((s) => s.me);
  const tx = useT();
  const lang = useLang((x) => x.lang);
  const { pending, syncing, lastSync, online } = useSync();
  // One report call for the whole week: today, yesterday and the trend all come from it.
  const [from, to] = useMemo(() => rangeFor('7d'), []);
  const today0 = to - DAY;
  const { rows, local, loading } = useSalesReport(from, to);
  const products = useProducts();
  const stock = useStockMap();
  const customers = useCustomers();
  const shift = useOpenShift();
  const [hidePlan, setHidePlan] = useState(() => sessionStorage.getItem('sd.hidePlan') === '1');
  const target = me?.store.settings.dailyTarget ?? 0;
  const role = useRole();
  const manage = canManage(role);
  const openUi = useUi((s) => s.set);
  const localOnly = useLocalOnly();
  const trialDays = me?.tenant.trial_ends_at ? Math.ceil((me.tenant.trial_ends_at - Date.now()) / DAY) : null;

  const week = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => ({ ts: from + i * DAY, net: 0, bills: 0 }));
    const sum = { bills: 0, net: 0, tax: 0 };
    const byPay: Record<string, number> = {};
    for (const r of rows) {
      const ts = r.hour * 3600000;
      const d = days[Math.floor((ts - from) / DAY)];
      if (d) { d.net += r.net; d.bills += r.bills; }
      if (ts >= today0) {
        sum.bills += r.bills; sum.net += r.net; sum.tax += r.tax;
        for (const [k, v] of Object.entries(r.by_pay)) byPay[k] = (byPay[k] ?? 0) + v;
      }
    }
    return { days, sum, byPay, yesterday: days[5]?.net ?? 0 };
  }, [rows, from, today0]);
  const { sum, byPay, days, yesterday } = week;
  const delta = yesterday > 0 ? Math.round(((sum.net - yesterday) / yesterday) * 100) : null;

  const todayBills = useMemo(() => local.filter((b) => b.ts >= today0), [local, today0]);
  const items = todayBills.reduce((s, b) => s + b.items.length, 0);
  const top = useMemo(() => {
    const m = new Map<number, { qty: number; amount: number }>();
    for (const b of todayBills) for (const l of b.items) {
      const sg = b.kind === 1 ? -1 : 1;
      const cur = m.get(l[0]) ?? { qty: 0, amount: 0 };
      cur.qty += sg * l[1]; cur.amount += sg * lineNet(l);
      m.set(l[0], cur);
    }
    const byId = new Map((products ?? []).map((p) => [p.id, p]));
    return [...m.entries()].filter(([, v]) => v.amount > 0).sort((a, b) => b[1].amount - a[1].amount).slice(0, 5)
      .map(([id, v]) => ({ id, ...v, product: byId.get(id) }));
  }, [todayBills, products]);

  const low = useMemo(() => (products ?? []).filter((p) => p.active && ((stock.get(p.id) ?? 0) <= 0 || (p.reorder != null && (stock.get(p.id) ?? 0) <= p.reorder))).slice(0, 5), [products, stock]);
  const khata = useMemo(() => (customers ?? []).reduce((a, c) => ({ due: a.due + Math.max(0, c.balance), n: a.n + (c.balance > 0 ? 1 : 0) }), { due: 0, n: 0 }), [customers]);

  const actions: Array<{ to: string; label: string; icon: LucideIcon; tint: Tint }> = [
    { to: '/billing', label: tr('New bill'), icon: ShoppingBag, tint: 'green' },
    { to: '/customers', label: tr('Customers'), icon: BookUser, tint: 'blue' },
    { to: '/day-close', label: tr('Day close'), icon: Wallet, tint: 'amber' },
    ...(manage ? [{ to: '/receive', label: tr('Receive'), icon: Inbox, tint: 'teal' as const }, { to: '/stock-take', label: tr('Stock take'), icon: ClipboardCheck, tint: 'violet' as const }] : []),
    { to: '/inventory', label: tr('Inventory'), icon: Package, tint: 'rose' },
  ];

  return (
    <div className="pb-10">
      {/* Sense Fresh home header: greeting, store name and the connection pill. */}
      <div className="flex items-center gap-3 px-4 pt-5 pb-4 sm:px-6 lg:px-8">
        <button aria-label="Open menu" onClick={() => openUi({ drawer: true })} className="-ml-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 lg:hidden"><Menu size={22} /></button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-ink-3">{tx(greeting())}, {me?.user.name?.split(' ')[0] ?? ''} · {new Date().toLocaleDateString(localeOf(lang), { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          <h1 className="truncate text-xl font-bold">{me?.store.name ?? tr('Home')}</h1>
        </div>
        <SyncPill compact />
      </div>
      <div className="flex max-w-6xl flex-col gap-5 px-4 sm:px-6 lg:px-8">
        {role === 'owner' && me && setupDone(me.store.setup_steps) < SETUP_STEPS.length && (
          <Link to="/setup" className="flex items-center gap-3 rounded-card border border-line bg-white p-4 shadow-card">
            <IconTile icon={ClipboardCheck} tint="solid" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{tx('Finish store setup · {n} of {m} done', { n: setupDone(me.store.setup_steps), m: SETUP_STEPS.length })}</div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-2"><div className="h-full rounded-full bg-primary" style={{ width: `${(me.store.setup_steps.length / 5) * 100}%` }} /></div>
              <div className="mt-1 text-xs text-ink-3">{tx('Store details, business info, bank and delivery hours. Each step saves on its own.')}</div>
            </div>
            <ChevronRight size={18} className="text-ink-3" />
          </Link>
        )}

        {me?.tenant.status === 'trial' && trialDays != null && !hidePlan && (
          <div className="flex gap-3 rounded-card bg-t-amber p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/70 text-t-amber-fg"><Bell size={20} /></span>
            <div className="flex-1">
              <div className="text-sm font-semibold">{tx('{plan} plan · {n} days of free trial left', { plan: planById(me.tenant.plan_id).name, n: trialDays })}</div>
              <div className="text-xs text-ink-2">{tx('Then {price} / month per store, or start free on Starter.', { price: formatMoney(planById(me.tenant.plan_id).monthly, { whole: true }) })} <Link to="/settings/plan" className="font-semibold underline underline-offset-2">{tx('See plans')}</Link></div>
            </div>
            <button aria-label="Dismiss" onClick={() => { setHidePlan(true); sessionStorage.setItem('sd.hidePlan', '1'); }} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-2 hover:bg-white/60"><X size={18} /></button>
          </div>
        )}

        {!localOnly && (pending > 0 || !online) && (
          <Card className="flex items-center gap-3 p-3 pl-4">
            <IconTile icon={RefreshCw} tint="blue" size={40} />
            <div className="flex-1">
              <div className="text-sm font-semibold">{tr('{0} {1} waiting to sync', { 0: pending, 1: pending === 1 ? tr('change') : tr('changes') })}</div>
              <div className="text-xs text-ink-3">{online ? (lastSync ? tr('Last synced {0}', { 0: new Date(lastSync).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) }) : tr('Not synced yet')) : tr('You are offline — billing keeps working.')}</div>
            </div>
            {online && <Button size="sm" variant="secondary" onClick={() => void syncNow()} disabled={syncing}>{syncing ? tr('Syncing…') : tr('Sync now')}</Button>}
          </Card>
        )}

        <section>
          <SectionLabel>{tx('Today')}</SectionLabel>
          <div className="mt-2 grid grid-cols-2 gap-3 lg:grid-cols-4 [&>*]:min-w-0">
            {/* Today's sales: white card, green trend. */}
            <div className="col-span-2 rounded-card border border-line bg-white p-4 shadow-card sm:p-5 lg:col-span-4 xl:col-span-2 xl:row-span-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="flex items-center gap-1 text-2xs font-semibold tracking-wider text-ink-3 uppercase"><IndianRupee size={12} />{tx('Today’s sales')}</p>
                  {loading && !rows.length ? <Skeleton className="mt-2 h-8 w-40" /> : <p className="num mt-1 text-3xl font-bold">{formatMoney(sum.net)}</p>}
                  <p className="text-xs text-ink-3">GST {formatMoney(sum.tax, { whole: true })} · {tx(sum.bills === 1 ? '1 bill' : '{n} bills', { n: sum.bills })}</p>
                </div>
                {delta != null && (
                  <span className={`inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-2xs font-semibold ${delta >= 0 ? 'bg-t-green text-t-green-fg' : 'bg-t-red text-danger'}`}>
                    {delta >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}{tx('{n}% vs yesterday', { n: Math.abs(delta) })}
                  </span>
                )}
              </div>
              <Trend days={days} />
              {target > 0 && <div className="mt-3">
                <div className="h-1.5 overflow-hidden rounded-full bg-surface-2"><div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, (sum.net / target) * 100)}%` }} /></div>
                <div className="mt-1 text-2xs text-ink-3">{tr('{0}% of daily target {1}', { 0: Math.round((sum.net / target) * 100), 1: formatMoney(target, { whole: true }) })}</div>
              </div>}
            </div>
            <StatCard tint="amber" icon={Hash} label={tx('Bills')} value={String(sum.bills)} />
            <StatCard tint="teal" icon={TrendingUp} label={tx('Avg bill')} value={formatMoney(sum.bills ? Math.round(sum.net / sum.bills) : 0, { whole: true })} />
            <StatCard tint="rose" icon={Package} label={tx('Items sold')} value={String(items)} />
            <Link to="/customers" className="min-w-0"><StatCard tint="blue" icon={BookUser} label={tx('Khata due')} value={formatMoney(khata.due, { whole: true })} sub={tx(khata.n === 1 ? '1 customer' : '{n} customers', { n: khata.n })} /></Link>
          </div>
        </section>

        <ShiftCard shift={shift} />
        {manage && <Alerts />}

        <section>
          <SectionLabel>{tx('Quick actions')}</SectionLabel>
          <div className="mt-2 grid grid-cols-4 gap-3 lg:grid-cols-8">
            {actions.map((a) => (
              <Link key={a.to} to={a.to} className="group flex flex-col items-center gap-1.5 text-center">
                <span className={`flex h-14 w-14 items-center justify-center rounded-2xl transition group-active:scale-95 ${tints[a.tint]}`}><a.icon size={22} aria-hidden /></span>
                <span className="text-xs font-medium text-ink-2">{tx(a.label)}</span>
              </Link>
            ))}
          </div>
        </section>

        {todayBills.length > 0 && (
          <section>
            <div className="mb-2 flex items-center justify-between"><SectionLabel>{tx('Recent bills')}</SectionLabel><Link to="/transactions" className="text-xs font-semibold text-primary">{tx('See all')}</Link></div>
            <Card className="divide-y divide-line overflow-hidden">
              {[...todayBills].sort((a, b) => b.ts - a.ts).slice(0, 5).map((b) => (
                <Link key={b.id} to="/transactions" className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-2 text-ink-3"><ReceiptText size={16} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="num block truncate text-sm font-semibold">{b.bill_no}{b.kind === 1 ? tr(' · return') : ''}</span>
                    <span className="block truncate text-xs text-ink-3">{new Date(b.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · {b.items.length} {b.items.length === 1 ? tr('item') : tr('items')} · {b.payments.map((p) => p[0].toUpperCase()).join('+')}</span>
                  </span>
                  <span className={`num shrink-0 text-sm font-bold ${b.kind === 1 ? 'text-danger' : ''}`}>{b.kind === 1 ? '−' : ''}{formatMoney(b.total)}</span>
                </Link>
              ))}
            </Card>
          </section>
        )}

        <div className="grid gap-5 lg:grid-cols-3 [&>*]:min-w-0">
          <section>
            <SectionLabel>{tx('Payment mix · today')}</SectionLabel>
            <Card className="mt-2 p-4"><PayRing byPay={byPay} /></Card>
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between"><SectionLabel>{tx('Top sellers · today')}</SectionLabel><Link to="/reports" className="text-xs font-semibold text-primary">{tx('See all')}</Link></div>
            <Card className="divide-y divide-line overflow-hidden">
              {top.length === 0 && <div className="px-4"><MiniEmpty icon={Sparkles} text={tx('Your best sellers show up after the first few bills.')} /></div>}
              {top.map((t, i) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                  <span className={`num flex h-8 w-8 items-center justify-center rounded-xl text-xs font-bold ${i === 0 ? 'bg-t-amber text-t-amber-fg' : 'bg-surface-2 text-ink-3'}`}>{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate font-semibold">{t.product?.name ?? `#${t.id}`}</span>
                  <span className="num text-xs text-ink-3">{formatQty(t.qty, t.product?.unit ?? tr('pc'))}</span>
                  <span className="num w-20 text-right font-bold">{formatMoney(t.amount, { whole: true })}</span>
                </div>
              ))}
            </Card>
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between"><SectionLabel>{tx('Low stock')}</SectionLabel><Link to="/inventory" className="text-xs font-semibold text-primary">{tx('Inventory')}</Link></div>
            <Card className="divide-y divide-line overflow-hidden">
              {products === undefined && <div className="flex flex-col gap-3 p-4"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-1/2" /></div>}
              {products && low.length === 0 && <div className="px-4"><MiniEmpty icon={Package} text={tx('Nothing below reorder level.')} /></div>}
              {low.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                  <span className="min-w-0 flex-1 truncate font-semibold">{p.name}</span>
                  {p.reorder != null && <span className="hidden text-xs text-ink-3 sm:inline">{tr('reorder {0}', { 0: formatQty(p.reorder, p.unit) })}</span>}
                  <span className="num w-20 text-right font-bold text-danger">{formatQty(stock.get(p.id) ?? 0, p.unit)}</span>
                </div>
              ))}
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
}

/** Seven-day sales area chart for the hero card. */
function Trend({ days }: { days: Array<{ ts: number; net: number }> }) {
  const tx = useT();
  const lang = useLang((x) => x.lang);
  const W = 300, H = 72;
  const max = Math.max(1, ...days.map((d) => d.net));
  const pts = days.map((d, i) => [(i / (days.length - 1)) * W, H - 6 - (Math.max(0, d.net) / max) * (H - 14)] as const);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  return (
    <div className="relative mt-4">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-20 w-full overflow-visible" role="img" aria-label="Sales over the last 7 days">
        <defs><linearGradient id="trendFill" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#1b5236" stopOpacity="0.18" /><stop offset="1" stopColor="#1b5236" stopOpacity="0" /></linearGradient></defs>
        <path d={`${line} L${W},${H} L0,${H} Z`} fill="url(#trendFill)" />
        <path d={line} fill="none" stroke="#1b5236" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {last && <circle cx={last[0]} cy={last[1]} r="4" fill="#fff" stroke="#1b5236" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />}
      </svg>
      <div className="mt-1 flex justify-between text-2xs font-semibold text-ink-3">
        {days.map((d, i) => <span key={d.ts} className={i === days.length - 1 ? 'text-ink' : ''}>{i === days.length - 1 ? tx('Today') : new Date(d.ts).toLocaleDateString(localeOf(lang), { weekday: 'short' })}</span>)}
      </div>
    </div>
  );
}

function PayRing({ byPay }: { byPay: Record<string, number> }) {
  const tx = useT();
  const parts = Object.entries(byPay).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const total = parts.reduce((a, [, v]) => a + v, 0);
  if (!total) return <MiniEmpty icon={Wallet} text={tx('Cash, UPI, card and khata split appears after your first bill.')} />;
  const R = 42, C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div className="mt-3 flex items-center gap-5">
      <div className="relative h-32 w-32 shrink-0">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r={R} fill="none" stroke="#eef1ec" strokeWidth="12" />
          {parts.map(([k, v]) => {
            const len = (v / total) * C;
            const el = <circle key={k} cx="50" cy="50" r={R} fill="none" stroke={PAY_COLORS[k] ?? '#78837c'} strokeWidth="12" strokeDasharray={`${Math.max(0, len - 1.5)} ${C}`} strokeDashoffset={-acc} strokeLinecap="butt" />;
            acc += len;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-2xs font-semibold text-ink-3">{tx('Total').toUpperCase()}</span><span className="num text-sm font-semibold">{formatMoney(total, { whole: true })}</span></div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {parts.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: PAY_COLORS[k] }} />
            <span className="flex-1 font-medium">{tx(PAY_LABELS[k] ?? k)}</span>
            <span className="num text-muted">{Math.round((v / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShiftCard({ shift }: { shift: ReturnType<typeof useOpenShift> }) {
  const tx = useT();
  if (shift === undefined) return null;
  const since = shift && new Date(shift.opened_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return (
    <Link to="/day-close" className="flex items-center gap-3 rounded-card border border-line bg-white p-4 shadow-card">
      <IconTile icon={Wallet} tint="amber" size={40} />
      <div className="min-w-0 flex-1">
        {shift ? <>
          <div className="flex items-center gap-2 text-sm font-semibold"><span className="h-2 w-2 animate-pulse rounded-full bg-success" />{tx('Shift open since {t}', { t: since || '' })}</div>
          <div className="truncate text-xs text-ink-3">{tr('Opened by {0} · float {1} · close the day to print the Z report', { 0: shift.opened_by, 1: formatMoney(shift.float, { whole: true }) })}</div>
        </> : <>
          <div className="text-sm font-semibold">{tx('No shift open')}</div>
          <div className="text-xs text-ink-3">{tx('Count the cash drawer to start the day.')}</div>
        </>}
      </div>
      <span className="hidden h-9 items-center rounded-full bg-primary-tint px-4 text-xs font-semibold text-primary sm:inline-flex">{tx(shift ? 'Day close' : 'Open shift')}</span>
      <ChevronRight size={18} className="text-ink-3" />
    </Link>
  );
}

function MiniEmpty({ icon: Icon, text }: { icon: LucideIcon; text: string }) {
  return (
    <div className="flex items-center gap-3 py-4 text-sm text-ink-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-2 text-ink-3"><Icon size={18} /></span>{text}
    </div>
  );
}

/** Things that need a manager's attention today: fresh stock expiring, supplier dues, quiet terminals. */
function Alerts() {
  const expiring = useExpiringCount();
  const suppliers = useSuppliers();
  const owed = isLocalOnly() ? [] : (suppliers ?? []).filter((x) => x.balance > 0);
  const [stale, setStale] = useState<string[]>([]);
  useEffect(() => {
    if (isLocalOnly()) return; // one till, and no calls on a phone-only store
    void api<{ terminals: Array<{ code: string; last_sync: number | null; current: boolean }> }>('/api/terminals')
      .then((r) => setStale(r.terminals.filter((t) => !t.current && t.last_sync != null && Date.now() - t.last_sync > 6 * 3600000).map((t) => t.code)), () => {});
  }, []);
  const items: Array<{ to: string; icon: LucideIcon; tint: Tint; title: string; sub: string }> = [];
  if (expiring > 0) items.push({ to: '/fresh', icon: AlarmClock, tint: 'red', title: `${expiring} fresh ${expiring === 1 ? 'batch' : 'batches'} expiring`, sub: tr('Sell, mark down or record wastage') });
  if (owed.length) items.push({ to: '/suppliers', icon: Truck, tint: 'teal', title: `${formatMoney(owed.reduce((a, x) => a + x.balance, 0), { whole: true })} to pay suppliers`, sub: `${owed.length} ${owed.length === 1 ? 'supplier' : 'suppliers'}` });
  if (stale.length) items.push({ to: '/settings', icon: MonitorSmartphone, tint: 'amber', title: `${stale.join(', ')} not synced for 6 h+`, sub: tr('Check the internet on that till') });
  if (!items.length) return null;
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {items.map((i) => (
        <Link key={i.to} to={i.to} className="flex items-center gap-3 rounded-card border border-line bg-white p-3 shadow-card">
          <IconTile icon={i.icon} tint={i.tint} size={40} />
          <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{i.title}</span><span className="block truncate text-xs text-ink-3">{i.sub}</span></span>
          <ChevronRight size={18} className="text-ink-3" />
        </Link>
      ))}
    </div>
  );
}
